import {
  completeQueryData,
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  defer,
  filter,
  first,
  from,
  map,
  of,
  pipe,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { Injectable } from '@angular/core';

import {
  DetailedOrder,
  PrintsOrderDetails,
  ProductInOrder,
  ProductOrderDetails,
  SingleProductInOrder,
} from '../types';
import {
  DigitalOrderState,
  GetMineOrdersGQL,
  GetMineOrdersQueryVariables,
  GetOrderDetailsGQL,
  GetOrdersForStatsGQL,
  GetPrintsOrderDetailsGQL,
  GetProductInOrderDetailsGQL,
  OrderContext,
  OrderForStatsFragment,
  OrderOnTheListFragment,
  PrintsOrderState,
  ProductOrderState,
  UpdateDigitalOrderGQL,
  UpdateOrderGQL,
  UpdatePrintOrderGQL,
  UpdateProductInOrderGQL,
} from '../graphql';

import Dexie from 'dexie';

import { DateTime } from 'luxon';

const database = new Dexie('zalamo-orders');

console.log(`

CREATE DATABASE


`);
type OrdersResult = {
  orders: OrderOnTheListFragment[];
  allOrdersCount: number;
};

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  rangeStart = new BehaviorSubject(
    localStorage.ordersStartDate && localStorage.ordersStartDate !== '0'
      ? DateTime.fromSeconds(+localStorage.ordersStartDate).toSeconds()
      : DateTime.local().minus({ months: 6 }).toSeconds(),
  );
  rangeEnd = new BehaviorSubject(DateTime.local().toSeconds());
  lastRange = null;

  constructor(
    private mineOrders: GetMineOrdersGQL,
    private ordersForStats: GetOrdersForStatsGQL,
    private orderDetails: GetOrderDetailsGQL,
    private updatePrintOrder: UpdatePrintOrderGQL,
    private getPrintsOrderDetailsQuery: GetPrintsOrderDetailsGQL,
    private updateOrder: UpdateOrderGQL,
    private productInOrderDetails: GetProductInOrderDetailsGQL,
    private updateProductInOrder: UpdateProductInOrderGQL,
    private updateDigitalOrder: UpdateDigitalOrderGQL,
  ) {
    try {
      database.version(10).stores({
        orders: `&orderId`,
      });
    } catch (e) {
      console.log(e);
    }
  }

  updateRange(range: { start: number; end: number }) {
    if (isNaN(range.start)) {
      range.start = DateTime.local().minus({ months: 3 }).toSeconds();
    }
    this.rangeStart.next(range.start);
    if (isNaN(range.end)) {
      range.end = DateTime.local().toSeconds();
    }
    this.rangeEnd.next(range.end ?? DateTime.local().toSeconds());
    this.lastRange = range;
  }

  getOrders(
    organizationId: string,
    context?: OrderContext,
    contextId?: string,
  ): Observable<Array<OrderOnTheListFragment>> {
    return from(database.table('orders').toArray())
      .pipe(
        catchError((er) => of([])),
        switchMap((ordersCache) => {
          console.log('ordersCache', ordersCache);
          return combineLatest([this.rangeEnd, this.rangeStart]).pipe(
            debounceTime(300),
            switchMap(([to, fromStart]) => {
              const query: GetMineOrdersQueryVariables = {
                organizationId,
              };
              if (!context) {
                query.range = {
                  from: Math.round(fromStart),
                  to: Math.round(to),
                };
              } else {
                query.context = context;
                query.contextId = contextId;
              }

              return this.loadOrders$(query).pipe(
                startWith(ordersCache),
                tap(async (orders) => {
                  await database.table('orders').clear();
                  database.table('orders').bulkPut(orders);
                }),
              );
            }),
          );
        }),
      )

      .pipe(
        map((orders) => {
          if (context) {
            return orders.filter(
              (order) =>
                order.context === context && order.contextId === contextId,
            );
          }
          return orders;
        }),
        shareReplay(1),
      );
  }

  getOrderFromCache(
    orderId: string,
    organizationId: string,
  ): Observable<OrderOnTheListFragment> {
    return from(
      database.table<OrderOnTheListFragment>('orders').get(orderId),
    ).pipe(
      catchError((er) => of(null)),
      switchMap((ordersCache) => {
        console.log('ordersCache', ordersCache);
        return combineLatest([this.rangeEnd, this.rangeStart]).pipe(
          debounceTime(300),
          switchMap(([to, fromStart]) =>
            this.loadOrders$({
              organizationId,
              range: {
                from: Math.round(fromStart),
                to: Math.round(to),
              },
            }).pipe(
              map((orders) => {
                return orders.find((order) => order.orderId === orderId);
              }),
              tap((x) => console.log(x)),
              startWith(ordersCache),
            ),
          ),
        );
      }),
    );
  }

  loadOrders$(baseVars: GetMineOrdersQueryVariables) {
    return defer(() => {
      // Single, long-lived watch keeps auto-updates flowing.
      const queryRef = this.mineOrders.watch({
        ...{
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true,
          // returnPartialData: true, // optional
        },
        variables: { ...baseVars, offset: 0 },
      });

      const fetchedOffsets = new Set<number>([0]);
      let pageSizeGuess = 0;
      let pagingInFlight = false;

      return queryRef.valueChanges.pipe(
        throwOnQueryError,
        tap((res) => {
          if (res.loading) return;

          const chunk = completeQueryData(res)?.orders;
          if (!chunk) return;

          const list = (chunk.orders ?? []) as OrderOnTheListFragment[];
          const total = chunk.allOrdersCount ?? list.length;

          if (!list.length || list.length >= total) return;

          if (!pageSizeGuess) pageSizeGuess = list.length;
          if (!pageSizeGuess) return;

          if (!pagingInFlight) {
            pagingInFlight = true;
            (async () => {
              try {
                for (
                  let offset = list.length;
                  offset < total;
                  offset += pageSizeGuess
                ) {
                  if (fetchedOffsets.has(offset)) continue;
                  fetchedOffsets.add(offset);
                  await queryRef.fetchMore({
                    variables: { ...baseVars, offset },
                    // No updateQuery needed; field policy merges the nested array.
                  });
                }
              } finally {
                pagingInFlight = false;
              }
            })();
          }
        }),
        filter(hasCompleteQueryData),
        map((res): OrdersResult => {
          const chunk = completeQueryData(res)?.orders;
          const orders = (chunk?.orders ?? []).filter(
            Boolean,
          ) as OrderOnTheListFragment[];
          const allOrdersCount = chunk?.allOrdersCount ?? orders.length;
          return { orders, allOrdersCount };
        }),
        filter(({ orders, allOrdersCount }) => orders.length >= allOrdersCount),
        concatMap(({ orders, allOrdersCount }) =>
          from(
            (async () => {
              await database.table('orders').clear();
              if (orders.length) {
                await database.table('orders').bulkPut(orders);
              }
              return orders;
            })(),
          ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    });
  }
  getOrdersForStats(
    organizationId: string,
    context?: OrderContext,
    contextId?: string,
  ): Observable<Array<OrderForStatsFragment>> {
    return this.ordersForStats
      .watch({ variables: { organizationId, context, contextId } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((apolloQueryResult) => {
          const {
            data: {
              orders: { orders },
            },
          } = completeQueryResult(apolloQueryResult);
          return orders;
        }),
      );
  }

  getOrder(orderId, force = false): Observable<DetailedOrder> {
    return this.orderDetails
      .watch({
        ...{
          fetchPolicy: force ? 'network-only' : 'cache-first',
        },
        variables: {
          orderId,
        },
      })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getOrder']),
      );
  }

  getPrintsOrderDetails(orderId): Observable<PrintsOrderDetails> {
    return this.getPrintsOrderDetailsQuery
      .watch({ variables: { orderId } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getPrintsOrderDetails']),
      );
  }

  getPrintsOrderDetailsByPrintOrderId(
    printsOrderId,
  ): Observable<PrintsOrderDetails> {
    return this.getPrintsOrderDetailsQuery
      .watch({ variables: { printsOrderId } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getPrintsOrderDetails']),
      );
  }

  getProductOrderDetails(orderId): Observable<ProductOrderDetails> {
    return of(null);
  }

  updatePrintsOrderState(
    query: { orderId?: string; printsOrderId?: string },
    currentState: PrintsOrderState,
  ) {
    return this.updatePrintOrder
      .mutate({
        variables: {
          ...query,
          update: {
            currentState,
          },
        },
      })
      .toPromise();
  }

  updateProductInOrderState(
    productInOrderId: string,
    update: ProductOrderState,
  ) {
    this.updateProductInOrder
      .mutate({
        variables: {
          productInOrderId,
          update: { currentState: update },
        },
      })
      .toPromise();
  }

  updateOrderState(orderId: string, state: Partial<DetailedOrder>) {
    const stateForServer = {
      ...state,
    };
    if (stateForServer.buyer) {
      delete stateForServer.buyer.buyerDetailsId;
    }
    this.getOrder(orderId)
      .pipe(
        switchMap((order) =>
          this.updateOrder.mutate({
            ...{
              optimisticResponse: {
                updateOrder: {
                  ...order,
                  ...state,
                  delivery: {
                    ...order.delivery,
                    ...state.delivery,
                    deliveryAddress: state.delivery
                      ? {
                          deliveryAddressId: null,
                          ...state.delivery.deliveryAddress,
                        }
                      : order.delivery.deliveryAddress,
                    deliveryDetailsId: order.delivery.deliveryDetailsId,
                    lockerDetails: state.delivery?.lockerDetails
                      ? state.delivery.lockerDetails
                      : order.delivery.lockerDetails,
                  },
                },
              },
            },
            variables: { orderId, update: stateForServer },
          }),
        ),
        first(),
      )
      .subscribe();
  }

  getProductInOrder(productInOrderId): Observable<SingleProductInOrder> {
    return this.productInOrderDetails
      .watch({ variables: { productInOrderId } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getProductInOrderDetails']),
      );
  }

  getProductInOrderDetails(productInOrderId): Observable<ProductInOrder> {
    return this.productInOrderDetails
      .watch({ variables: { productInOrderId } })
      .valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getProductInOrderDetails']),
        map((productDetails) => ({
          ...productDetails,
          configDump: ((dump) => {
            const parsed = JSON.parse(dump);
            return {
              ...parsed,
              configuration: JSON.parse(parsed.configuration),
            };
          })(productDetails.dump),
          configuration: JSON.parse(productDetails.configuration),
          photos: productDetails.photos,

          product: {
            ...productDetails.product,
            config: JSON.parse(productDetails.product.config),
          },
        })),
      );
  }

  updateDigitalOrderState(
    digitalOrderId: string,
    currentState: DigitalOrderState,
  ) {
    this.updateDigitalOrder
      .mutate({ variables: { digitalOrderId, update: { currentState } } })
      .toPromise();
  }
}
